// Sprint 0078 — Web Push subscription helpers.
//
// Flow:
//   1. ensurePushSubscription() called once after login
//   2. Check support + permission
//   3. Get VAPID public key from backend
//   4. pushManager.subscribe() returns a subscription
//   5. POST it to /push/subscribe
//
// User can revoke any time; we listen for that and call /push/unsubscribe.

import apiClient from '@/shared/api/client'

// ── Helpers ────────────────────────────────────────────────────────────────

function urlBase64ToUint8Array(b64: string): Uint8Array {
  // VAPID keys are base64url; pushManager needs Uint8Array.
  const padding = '='.repeat((4 - (b64.length % 4)) % 4)
  const base64 = (b64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  const arr = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i)
  return arr
}

function arrayBufferToBase64Url(buf: ArrayBuffer | null): string {
  if (!buf) return ''
  const bytes = new Uint8Array(buf)
  let s = ''
  for (let i = 0; i < bytes.byteLength; i++) s += String.fromCharCode(bytes[i])
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

// ── Public API ─────────────────────────────────────────────────────────────

export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

export function getPushPermission(): NotificationPermission {
  if (!isPushSupported()) return 'denied'
  return Notification.permission
}

/** Request permission. Returns the user's choice. */
export async function requestPushPermission(): Promise<NotificationPermission> {
  if (!isPushSupported()) return 'denied'
  if (Notification.permission === 'granted') return 'granted'
  if (Notification.permission === 'denied') return 'denied'
  return Notification.requestPermission()
}

/**
 * Register the current device for push.
 * Idempotent — safe to call on every app load. If already subscribed,
 * just re-syncs the subscription to the backend.
 */
export async function ensurePushSubscription(): Promise<{
  ok: boolean
  reason?: string
}> {
  try {
    if (!isPushSupported()) return { ok: false, reason: 'not-supported' }
    if (Notification.permission !== 'granted') {
      return { ok: false, reason: 'not-granted' }
    }

    const reg = await navigator.serviceWorker.ready

    // Get VAPID public key from server
    const keyResp = await apiClient.get<{ key: string }>('/push/vapid-public-key')
    const vapidKey = keyResp.data.key
    const applicationServerKey = urlBase64ToUint8Array(vapidKey)

    // Is there an existing subscription?
    let sub = await reg.pushManager.getSubscription()
    if (sub) {
      // Refresh to backend (in case keys rotated)
      await syncSubscription(sub)
      return { ok: true }
    }

    // Subscribe
    // Cast to BufferSource — TS lib types are strict, but Uint8Array is the
    // standard input here per the Push API spec.
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true, // browsers require this
      applicationServerKey: applicationServerKey as unknown as BufferSource,
    })
    await syncSubscription(sub)
    return { ok: true }
  } catch (e) {
    console.error('[push] ensurePushSubscription failed:', e)
    return { ok: false, reason: (e as Error)?.message }
  }
}

async function syncSubscription(sub: PushSubscription): Promise<void> {
  const p256dh = arrayBufferToBase64Url(sub.getKey('p256dh'))
  const auth = arrayBufferToBase64Url(sub.getKey('auth'))
  await apiClient.post('/push/subscribe', {
    endpoint: sub.endpoint,
    keys: { p256dh, auth },
  })
}

/** Cancel push on this device. */
export async function unsubscribePush(): Promise<void> {
  if (!isPushSupported()) return
  const reg = await navigator.serviceWorker.ready
  const sub = await reg.pushManager.getSubscription()
  if (!sub) return
  await apiClient.post('/push/unsubscribe', { endpoint: sub.endpoint })
  await sub.unsubscribe()
}

/** Fire a test push to your own subscriptions. */
export async function sendTestPush(): Promise<void> {
  await apiClient.post('/push/test')
}
